import { useEffect, useState, type FormEvent } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import { SELECTOR_PAGE_SIZE } from '../../lib/constants'
import { confirmDialog, toast } from '../../lib/dialogs'
import { fromLocalInput, toLocalInput } from '../../lib/datetime'
import {
  CalendarEventTypeLabel,
  type CalendarEventDto,
  type CalendarEventType,
  type OpportunityListItem,
  type OpportunityListResponse,
  type UserListItem,
} from '@toga/shared'

interface Props {
  initial: CalendarEventDto | null
  defaultStartAt?: Date
  defaultCaseId?: string | null
  onClose: () => void
  onSaved: () => void
}

export default function EventFormModal({
  initial,
  defaultStartAt,
  defaultCaseId,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation(['agenda', 'common'])
  const isEdit = initial !== null

  const [title, setTitle] = useState(initial?.title ?? '')
  const [type, setType] = useState<CalendarEventType>(initial?.type ?? 'OTRO')
  const [startAt, setStartAt] = useState(
    initial ? toLocalInput(new Date(initial.startAt)) : toLocalInput(defaultStartAt ?? new Date())
  )
  const [endAt, setEndAt] = useState(
    initial?.endAt
      ? toLocalInput(new Date(initial.endAt))
      : toLocalInput(
          defaultStartAt
            ? new Date(defaultStartAt.getTime() + 60 * 60 * 1000)
            : new Date(Date.now() + 60 * 60 * 1000)
        )
  )
  const [allDay, setAllDay] = useState(initial?.allDay ?? false)
  const [location, setLocation] = useState(initial?.location ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [isPrivate, setIsPrivate] = useState(initial?.isPrivate ?? false)
  const [opportunityId, setCaseId] = useState(initial?.opportunityId ?? defaultCaseId ?? '')
  const [attendeeIds, setAttendeeIds] = useState<string[]>(
    initial?.attendees.map((a) => a.userId) ?? []
  )

  const [cases, setCases] = useState<OpportunityListItem[] | null>(null)
  const [users, setUsers] = useState<UserListItem[] | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [casesResp, usersResp] = await Promise.all([
          // pageSize máx del backend = 100 (antes 200 → 400 y rompía el modal).
          api
            .get<OpportunityListResponse>(`/opportunities?pageSize=${SELECTOR_PAGE_SIZE}`)
            .catch(() => ({ items: [], total: 0, page: 1, pageSize: 100 })),
          api.get<{ users: UserListItem[] }>('/users').catch(() => ({ users: [] })),
        ])
        setCases(casesResp.items)
        setUsers((usersResp as { users: UserListItem[] }).users)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('eventForm.errors.load'))
      }
    })()
  }, [])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload = {
        title,
        type,
        startAt: fromLocalInput(startAt).toISOString(),
        endAt: allDay ? null : fromLocalInput(endAt).toISOString(),
        allDay,
        location: location || null,
        description: description || null,
        isPrivate,
        opportunityId: opportunityId || null,
        attendeeIds,
      }
      if (isEdit && initial) {
        await api.patch(`/calendar-events/${initial.id}`, payload)
      } else {
        await api.post('/calendar-events', payload)
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const firstFieldErr = body?.issues?.fieldErrors
          ? Object.values(body.issues.fieldErrors).flat().find(Boolean)
          : undefined
        setError(firstFieldErr ?? body?.error ?? t('eventForm.errors.save'))
      } else {
        setError(t('eventForm.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async () => {
    if (!initial) return
    if (!(await confirmDialog({ message: t('eventForm.confirmDelete'), tone: 'danger' }))) return
    try {
      await api.delete(`/calendar-events/${initial.id}`)
      onSaved()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('eventForm.errors.generic'))
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('eventForm.title.edit') : t('eventForm.title.new')}
      maxWidth="max-w-xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">{t('eventForm.fields.title')}</label>
            <input
              required
              minLength={2}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('eventForm.fields.type')}</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as CalendarEventType)}
              className={inputCls}
            >
              {Object.entries(CalendarEventTypeLabel).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 items-end">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('eventForm.fields.start')}</label>
            <input
              type="datetime-local"
              required
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('eventForm.fields.end')}</label>
            <input
              type="datetime-local"
              disabled={allDay}
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm t-secondary">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          {t('eventForm.fields.allDay')}
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('eventForm.fields.location')}</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t('eventForm.placeholders.location')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('eventForm.fields.opportunity')}</label>
            <select value={opportunityId} onChange={(e) => setCaseId(e.target.value)} className={inputCls}>
              <option value="">{t('eventForm.options.noOpportunity')}</option>
              {cases?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('eventForm.fields.attendees')}</label>
          <select
            multiple
            value={attendeeIds}
            onChange={(e) => setAttendeeIds(Array.from(e.target.selectedOptions, (o) => o.value))}
            size={4}
            className={inputCls}
          >
            {users?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.fullName}
              </option>
            ))}
          </select>
          <p className="text-xs t-faint mt-1">{t('eventForm.hints.attendees')}</p>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('eventForm.fields.notes')}</label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={inputCls}
          />
        </div>

        <label className="inline-flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={isPrivate}
            onChange={(e) => setIsPrivate(e.target.checked)}
          />
          {t('eventForm.fields.private')}
        </label>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          {isEdit && initial?.canEdit && (
            <button
              type="button"
              onClick={onDelete}
              className="px-3 py-2 rounded-lg text-sm bg-rose-500/10 text-rose-700 dark:text-rose-300 hover:bg-rose-500/15 flex items-center gap-1.5"
            >
              <Trash2 size={14} /> {t('common:actions.delete')}
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? t('common:actions.save') : t('eventForm.actions.create')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
